'use strict';
import DataSetRowIdentity from '../../../services/dataset-row-identity';

/** *
 * This `MxDataQueryList` can be used for getting data with all preprocessors (paging, sorting, filtering).
 *
 * How to use it:
 *
 *    Basic example: In this case you'll get basic preprocessors like:
 *        - mx.SolutionBuilderAgent.SortingPreprocessor
 *        - mx.SolutionBuilderAgent.PagingPreprocessor
 *        - mx.SolutionBuilderAgent.SearchPreprocessor
 *
 *        Let's create an example controller `MyController`.
 *
 *        MyController.$inject = ['$injector', '$scope'];
 *        MyController.prototype = Object.create(DataQueryList.prototype);
 *        function MyController($injector, $scope) {
 *			var vm = this;
 *			vm.type = mx.SolutionBuilderAgent.ListingType.list;  // For example for List
 *			this.initialize($injector, $scope); // init
 *			vm.load(); // This method should be called just ones. If you need to refresh data use `vm.reload();`
 *
 *			// You'll get data in `vm.data` which you can use in templates.
 *		}
 *
 *
 *    If you need to create/use not basic preprocessor (e.g. DateRange for calendar). You can do it like this:
 *
 *        Let's create an example controller `MyNotBasicController`.
 *
 *        MyNotBasicController.$inject = ['$injector', '$scope'];
 *        MyNotBasicController.prototype = Object.create(DataQueryList.prototype);
 *        function MyNotBasicController($injector, $scope) {
 *			var vm = this;
 *			vm.type = mx.SolutionBuilderAgent.ListingType.calendar;
 *			vm.preInitialize($injector, $scope);	// here we use `preInitialize()` instead of `initialize()`
 *									 				// so we can define preprocessors on our own instead of basics.
 *			// Init our own preprocessors instead of basics
 *			 vm.initPreprocessors([
 *				 mx.SolutionBuilderAgent.DateRangePreprocessor,
 *				 mx.SolutionBuilderAgent.SearchPreprocessor
 *			 ]);
 *			vm.load(); // This method should be called just ones. If you need to refresh data use `vm.reload();`
 *
 *			// You'll get data in `vm.data` which you can use in templates.
 *		}
 *
 **/
export default function MxDataQueryList() {}

let eventService;
let injector;
let _allSelectedState;

MxDataQueryList.prototype = {
	get wholeDatasetSelected() {
		return this.pagingPreprocessor.count !== -1 && this.contextService.selectedObjects.length === this.pagingPreprocessor.count;
	},

	get allSelectedState() {
		return _allSelectedState;
	},
	set allSelectedState(newValue) {
		_allSelectedState = newValue;
		if (!newValue) {
			this.clearSelection();
		}
	},
};

MxDataQueryList.prototype.initPreprocessors = function (preprocessors) {
	const that = this;
	const DataQueryExecutor = injector.get('mx.SolutionBuilderAgent.DataQueryExecutor');
	const ODataQueryExecutor = injector.get('mx.SolutionBuilderAgent.ODataQueryExecutor');
	const ClientQueryExecutor = injector.get('mx.SolutionBuilderAgent.ClientQueryExecutor');

	that.preprocessors = [];
	that.preprocessorsByName = {};
	const serviceInfo = that.parameters.dataQuery.ServiceInfo;
	that.parameters.dataQueryList = that;

	if (!serviceInfo) {
		that.dataQueryExecutor = new DataQueryExecutor();
	} else if (serviceInfo.SupportedOperationTypes & 4 /* Supported odata operation*/) {
		that.dataQueryExecutor = new ODataQueryExecutor();
	} else if (serviceInfo.SupportedOperationTypes & 2 /* Supported list operation*/) {
		that.dataQueryExecutor = new ClientQueryExecutor();
	}

	const executorPreprocessors = that.dataQueryExecutor.init(that.type, that.parameters);
	const types = that.parameters.isMultiFragment ? [] : that.dataQueryExecutor.getTypes();

	//Init actions
	this.contextService.val('SolutionBuilderAgent.ObjectTypes', types);
	this.contextService.actionsContext = new mx.shell.ActionsContext(
		types,
		this.contextService.actionsContext ? this.contextService.actionsContext.extraActions : [],
		mx.shell.ActionUiZone.SearchPage,
		that.parameters.isMultiFragment
	);

	preprocessors = preprocessors || [];
	preprocessors = preprocessors.concat(executorPreprocessors);

	preprocessors.forEach((Preprocessor) => {
		const preprocessor = new Preprocessor();

		switch (preprocessor.type) {
			case mx.SolutionBuilderAgent.PreprocessorType.Sorting:
				that.sortingPreprocessor = preprocessor;
				if (preprocessor instanceof injector.get('mx.SolutionBuilderAgent.SortingPreprocessor')) {
					that.preprocessorsByName.sortingPreprocessor = preprocessor;
				} else if (preprocessor instanceof injector.get('mx.SolutionBuilderAgent.SortingGridPreprocessor')) {
					that.preprocessorsByName.sortingGridPreprocessor = preprocessor;
				}
				break;
			case mx.SolutionBuilderAgent.PreprocessorType.QuickFilter:
				that.quickFilterPreprocessor = preprocessor;
				that.preprocessorsByName.quickFilterPreprocessor = preprocessor;
				break;
			case mx.SolutionBuilderAgent.PreprocessorType.Paging:
				that.pagingPreprocessor = preprocessor;
				that.pagingPreprocessor.getDataCount = function () {
					return that.getDataCount().then(function (c) {
						preprocessor.count = c;
						that.parameters.totalCount = c;
						return c;
					});
				};
				that.preprocessorsByName.pagingPreprocessor = preprocessor;
				break;
			case mx.SolutionBuilderAgent.PreprocessorType.Search:
				that.searchPreprocessor = preprocessor;
				that.preprocessorsByName.searchPreprocessor = preprocessor;
				break;
			case mx.SolutionBuilderAgent.PreprocessorType.Structure:
				that.structurePreprocessor = preprocessor;
				that.preprocessorsByName.structurePreprocessor = preprocessor;
				break;
			case mx.SolutionBuilderAgent.PreprocessorType.ColumnDefinition:
				that.columnDefinitionPreprocessor = preprocessor;
				that.preprocessorsByName.columnDefinitionPreprocessor = preprocessor;
				break;
			case mx.SolutionBuilderAgent.PreprocessorType.Grouping:
				that.groupingPreprocessor = preprocessor;
				that.preprocessorsByName.groupingPreprocessor = preprocessor;
				break;
			case mx.SolutionBuilderAgent.PreprocessorType.DateRange:
				that.dateRangePreprocessor = preprocessor;
				that.preprocessorsByName.dateRangePreprocessor = preprocessor;
				break;
		}

		preprocessor.init(that.dataQueryExecutor, that.parameters, that);

		that.preprocessors.push(preprocessor);
	});
};

MxDataQueryList.prototype.load = async function () {
	const that = this;
	that.loading = true;

	that.dataModel = that.parameters.dataModel;
	that.columns = that.parameters.dataQuery.Columns || [];

	await Promise.all(that.preprocessors.map((preprocessor) => preprocessor.load(that.parameters.id, that.parameters.dataQueryId, that.parameters)));
	that.addChangedHandlers();
	if (!that.lastDebouncedReloadTime) {
		return await that.reload();
	}
};

MxDataQueryList.prototype.preInitialize = function ($injector, $scope) {
	const that = this;

	injector = $injector;

	this.dataModelService = $injector.get('mx.SolutionBuilderAgent.DataModelService');
	this.extensions = JSON.parse(this.parameters.dataModelOptions || '{}');
	this.contextService = $injector.get('mx.shell.ContextService');
	this.actionExecutorService = $injector.get('mx.shell.ActionExecutorService');
	this.sidePanelActionsService = $injector.get('mx.shell.SidePanelActionsService');
	this.dialogOpenerService = $injector.get('mx.SolutionBuilderAgent.DialogOpenerService');
	this.personalizationService = $injector.get('mx.SolutionBuilderAgent.PersonalizationService');

	eventService = $injector.get('mx.shell.EventService');
	eventService.subscribeTo(
		mx.shell.EventCodes.Search,
		function (event, searchOptions) {
			const searchPageUiToken = that.contextService.val('globalUiToken');

			if (searchOptions.dataQueryList !== that) {
				return;
			}

			if (searchOptions) {
				if (searchOptions.globalSearch && !searchOptions.uiToken) {
					searchOptions.uiToken = searchPageUiToken;
					const searchProviderService = $injector.get('mx.shell.SearchProviderService');

					searchProviderService.uiToken = searchPageUiToken;
				}
				if (searchOptions.uiToken === searchPageUiToken) {
					that.searchPreprocessor.callChanged();
				}
			} else {
				that.searchPreprocessor.callChanged();
			}
		},
		$scope
	);

	eventService.subscribeTo(
		mx.SolutionBuilderAgent.Events.SearchPageDataChanged,
		function (ev, eventParams) {
			if ((eventParams || {}).affectedEntity) {
				that.highlightItem = eventParams.affectedEntity;
			}

			that.clearSelection();
		},
		$scope
	);

	that.loading = true;

	const _debouncedReload = $.debounce(50, function () {
		if (that.lastDebouncedReloadTime > that.lastReloadTime) {
			// if reload was executed directly during debounce period then do not execute reload
			that.reload.apply(that, arguments);
		}
	});

	that.debouncedReload = function () {
		that.lastDebouncedReloadTime = Date.now();
		_debouncedReload.apply(that, arguments);
	};
};

MxDataQueryList.prototype.initialize = function ($injector, $scope, customPreprocessors) {
	const that = this;

	that.preInitialize($injector, $scope);

	that.initPreprocessors(
		customPreprocessors || [
			injector.get('mx.SolutionBuilderAgent.SortingPreprocessor'),
			injector.get('mx.SolutionBuilderAgent.PagingPreprocessor'),
			injector.get('mx.SolutionBuilderAgent.SearchPreprocessor'),
			injector.get('mx.SolutionBuilderAgent.StructurePreprocessor'),
			injector.get('mx.SolutionBuilderAgent.GroupingPreprocessor'),
			injector.get('mx.SolutionBuilderAgent.QuickFilterPreprocessor'),
		]
	);
};

MxDataQueryList.prototype.addChangedHandlers = function () {
	const that = this;

	that.preprocessors.forEach(function (preprocessor) {
		preprocessor.addChangedHandler(function () {
			if (
				(preprocessor.type === mx.SolutionBuilderAgent.PreprocessorType.QuickFilter ||
					preprocessor.type === mx.SolutionBuilderAgent.PreprocessorType.Sorting ||
					preprocessor.type === mx.SolutionBuilderAgent.PreprocessorType.Grouping ||
					preprocessor.type === mx.SolutionBuilderAgent.PreprocessorType.Search) &&
				that.pagingPreprocessor
			) {
				that.pagingPreprocessor.clear();
			}

			if (preprocessor.type === mx.SolutionBuilderAgent.PreprocessorType.QuickFilter) {
				that.parameters.quickFilters = preprocessor.filters;
			}

			that.parameters.data = undefined;

			that.debouncedReload(true);
		});
	});
};

MxDataQueryList.prototype.setData = function (data) {
	const self = this;
	self.dataModelService.extendSourceResult(self.dataModel, 'DataQuery.return', self.dataModel.DataQuery, 'return', data);
	self.data = self.dataModel.DataQuery.return;
};

MxDataQueryList.prototype.reload = async function (dontClearSelection) {
	const that = this;

	if (that._destroyed) {
		return that.parameters.data;
	}
	const eventService = injector.get('mx.shell.EventService');
	const $q = injector.get('$q');

	that.loading = true;
	that.lastReloadTime = Date.now();
	const currentReloadTime = that.lastReloadTime; // save last ReloadTime in the scope for cases when several requests are executed. In this case only the latest execution will be finished

	const currentSearch = injector.get('mx.shell.SearchProviderService').currentSearch();
	Object.defineProperty(currentSearch, 'dataQueryList', { writable: true, enumerable: false, value: that });

	try {
		if (that.parameters.data) {
			// This block was done to avoid useless reloading from server.

			that.setData(that.parameters.data);

			that.preprocessors.forEach(function (preprocessor) {
				preprocessor.process(that.data, that.parameters.totalCount);
			});

			if (!Array.isArray(that.parameters.data) || that.parameters.data.length === 0) {
				that.noFilteredData = that._hasSearchFilters();
			} else {
				that.noFilteredData = false;
			}

			that.loading = false;
			return $q.when(that.parameters.data);
		}

		if (!dontClearSelection) {
			that.clearSelection();
		}

		// TODO: tmp implementation for $inactive
		const inactive = that.dataModel.DataQuery[mx.SolutionBuilderAgent.DataModelSystemProperty.Inactive] === true;

		if (inactive) {
			that.loading = false;
			return $q.when(that.parameters.data);
		}

		const parts = await that._getParts();
		const result = await that.dataQueryExecutor.run(parts);

		if (currentReloadTime !== that.lastReloadTime) {
			return;
		}

		that.parameters.data = result.data;
		that.parameters.totalCount = result.totalCount;

		await Promise.all(that.preprocessors.map((preprocessor) => preprocessor.process(result.data, result.totalCount)));

		if (result && Array.isArray(result.data)) {
			result.data.forEach(function (item) {
				Object.defineProperty(item, mx.SolutionBuilderAgent.DataModelSystemProperty.Item, {
					get: function () {
						return this;
					},
					set: function () {},
				});
			});
		}

		that.setData(result.data);

		if (dontClearSelection) {
			if (Array.isArray(that.contextService.selectedObjects)) {
				(that.data || []).forEach(function (newItem) {
					that.contextService.selectedObjects.some((oldItem, key) => {
						if (DataSetRowIdentity.equal(newItem, oldItem)) {
							newItem.__isSelected = true;
							that.contextService.selectedObjects[key] = newItem;
							return true;
						}
					});
				});
			} else {
				that.clearSelection();
			}
		}

		if (result && (!Array.isArray(result.data) || result.data.length === 0)) {
			that.noFilteredData = that._hasSearchFilters();
		} else {
			that.noFilteredData = false;
		}

		if (!this._destroyed) {
			that.contextService.val('SolutionBuilderAgent.DataSetView.Data', that.data);
			eventService.dispatchEvent(mx.SolutionBuilderAgent.Events.SearchPageReloaded, { dataQueryList: that });
		}
	} catch (e) {
		mx.logCatch(e);
	}

	that.loading = false;
	return $q.when(that.parameters.data);
};

MxDataQueryList.prototype.$onDestroy = function () {
	this._destroyed = true;
};

MxDataQueryList.prototype._hasSearchFilters = function () {
	const searchService = injector.get('mx.shell.SearchProviderService');
	const currentSearch = searchService.currentSearch() || {};
	const { conditions } = currentSearch.searchForm;
	return currentSearch.keyword || conditions?.length || hasAdditionalFilters(searchService, currentSearch) || this.quickFilterPreprocessor?.color;
};

function hasAdditionalFilters(searchService, currentSearch) {
	const currentContext = searchService.currentContext();
	if (!currentContext) {
		return false;
	}

	const baseFilter = searchService.getBaseQueryId(currentContext.parameters);
	const hasBaseFilter = (currentSearch.filters || []).indexOf(baseFilter) >= 0;
	return ((currentSearch.filters || []).length - hasBaseFilter ? 1 : 0) > 0;
}

MxDataQueryList.prototype._getParts = async function (preprocessorFilterFunc) {
	const $q = injector.get('$q');
	const parts = [];

	let filteredPreprocessors = this.preprocessors;
	if (preprocessorFilterFunc && typeof preprocessorFilterFunc === 'function') {
		filteredPreprocessors = filteredPreprocessors.filter((preprocessor) => preprocessorFilterFunc(preprocessor));
	}

	for (const preprocessor of filteredPreprocessors) {
		const part = await preprocessor.build(this.parameters);
		if (part) {
			parts.push(part);
		}
	}

	return $q.when(parts);
};

MxDataQueryList.prototype.getDataCount = function () {
	const that = this;

	return that._getParts().then(function (parts) {
		return that.dataQueryExecutor.getDataCount(parts, that.parameters.id, that.parameters.dataQueryId);
	});
};

MxDataQueryList.prototype.clearSelection = function () {
	this.contextService.selectedObjects.forEach(function (item) {
		item.__isSelected = false;
	});
	this.contextService.selectedObjects = [];
	if (_allSelectedState) {
		_allSelectedState = false;
	}
};

MxDataQueryList.prototype.selectionChanged = function (currentPageSelectedItems) {
	const self = this;
	const rowIdentity = DataSetRowIdentity;

	if (!self.pagingPreprocessor) {
		self.contextService.selectedObjects = currentPageSelectedItems;
		return;
	}

	const selectedObjects = self.contextService.selectedObjects;

	const newSelectedItems = _.uniqBy(currentPageSelectedItems, rowIdentity.uniqBy).filter(function (item) {
		return !_.find(selectedObjects, function (sItem) {
			return rowIdentity.equal(sItem, item);
		});
	});

	if (newSelectedItems.length) {
		selectedObjects.splice.apply(selectedObjects, [selectedObjects.length, 0].concat(newSelectedItems));
	}

	self.contextService.selectedObjects = _.filter(selectedObjects, '__isSelected');
};

MxDataQueryList.prototype.itemRightClickHandler = function (item) {
	const self = this;
	if (self.parameters.isMultiFragment) {
		return injector.get('$q').when([]);
	}
	let selectedObjects = item ? [].concat(item) : this.contextService.selectedObjects;
	if (this.contextService.selectedObjects.length > 1) {
		// mean multi select and we should skip item
		selectedObjects = this.contextService.selectedObjects;
	}

	if (!selectedObjects.length) {
		return injector.get('$q').when([]);
	}

	return this.sidePanelActionsService.calculateCommonActions(selectedObjects).then(function (actions) {
		return actions.map(function (action) {
			return {
				icon: action.icon,
				label: action.title,
				onItemClick: function () {
					if (selectedObjects.length === 1 && action.view && !action.view.isWizard) {
						action.view.displayName = selectedObjects[0]['Sys-DisplayName'] || selectedObjects[0]._displayName;
						action.view.name = selectedObjects[0].Name || selectedObjects[0]._name;
					}

					action.useSameView = false;
					self.actionExecutorService.execute(action, selectedObjects).catch(mx.logCatch);
				},
			};
		});
	});
};

MxDataQueryList.prototype.itemDblClickHandler = function (entity) {
	if (entity) {
		const self = this;
		this.sidePanelActionsService.calculateCommonActions([entity]).then(function (actions) {
			const actionToRun = actions.find(function (a) {
				return a.name.indexOf('ActionEdit') > -1 || a.name === 'AdvancedDetails';
			});
			if (actionToRun) {
				self.actionExecutorService.execute(actionToRun, [entity]).catch(mx.logCatch);
			}
		});
	}
};

MxDataQueryList.prototype.itemClickHandler = async function (item) {
	const that = this;

	const view = await that.dialogOpenerService.openPreview(item, {
		selectedObjects: [item],
		navigationId: that.parameters.dataModel.Context.NavigationId,
		searchPageWidgetId: that.parameters.id,
		hideActions: that.parameters.isMultiFragment,
	});

	eventService.dispatchEvent(mx.SolutionBuilderAgent.Events.DataQueryListItemClicked, item);
	if (view) {
		eventService.dispatchEvent(mx.SolutionBuilderAgent.Events.TrackViewTelemetry, { eventName: `Search Page: item clicked`, view });
	}
};

MxDataQueryList.prototype._selectEntireDatasetProgressHandler = function (e) {
	if (e && e.lengthComputable) {
		this.contextService.selectedObjectsLoadingProgress = parseInt((e.loaded / e.total) * 100, 10);
	}
};

MxDataQueryList.prototype.selectEntireDataset = async function () {
	this.contextService.selectedObjectsLoading = true;
	this.contextService.selectedObjectsLoadingProgress = 0;

	if (this.dataModel.DataQuery[mx.SolutionBuilderAgent.DataModelSystemProperty.Inactive] === true) {
		return;
	}

	const parts = await this._getParts((preprocessor) => preprocessor.type !== 'paging');

	const result = await this.dataQueryExecutor.run(parts, this._selectEntireDatasetProgressHandler.bind(this));

	const objects = result.data.map((object) => {
		if (this.parameters.data) {
			const objectsFromParams = this.parameters.data.filter((paramObject) => paramObject._id === object._id);
			object = objectsFromParams[0] || object;
		}
		object.__isSelected = true;
		return object;
	});

	const newObjects = objects.filter((object) => !this.contextService.selectedObjects.some((selObject) => DataSetRowIdentity.equal(selObject, object)));

	this.contextService.selectedObjects = this.contextService.selectedObjects.concat(newObjects);
	this.contextService.selectedObjectsLoadingProgress = 100;
	this.contextService.selectedObjectsLoading = false;
	return injector.get('$q').when();
};
